'use client';
import { useTranslations } from 'next-intl';
import { CatHeading, CatDrawer, CatButton } from 'components/blocks-components';
import KitDetailsTableBody from './kit-details-table-body';
import { CurrentItemsWithPrice } from 'services/fbtItems/types';
import styles from '../customize-kit-button/customize-kit-button.module.scss';
import KitMobile from './kit-mobile';
import { SkeletonLoader, useBreakpoint } from 'components/pcc-components';
import { DEBOUNCE_TIME_MILLISECONDS, PAGE_BREAKPOINTS } from 'constants/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DealerPriceAvailabilityResponse,
  ProductDealerPriceAvailability
} from 'services/dealerPriceAvailability/types';
import { useQuickToast } from 'hooks/use-toast';
import { DealerResponse } from 'services/dealer';
import { LocationResponse } from 'services/location/types';
import cx from 'classnames';
import { CartResponse, OrderItemsResponseKit } from 'services/cart/types';
import { fetchPrice, fetchPriceHeadless } from 'services/parts-diagram/actions';
import { getSubTotal } from 'services/subTotal/subTotal';
import { debounce } from '@cat-ecom/common/utils/commonUtils';
import { useCoreContext } from 'providers/core-provider';
import { useAddToCartItemEvent } from 'hooks/analytics/use-add-to-cart-item';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import {
  getFormattedItemData,
  getFormattedSearchSelectItem
} from 'components/product-comparison-grid/product-comparison-card/utils';
import { PRIME_PDP_KITS } from '../../constants/constants';
import {
  checkIsRiyal,
  getRiyalFormattedPrice
} from 'app/[lang]/[esite]/utils/paymentUtils';
import { transformHeadlessToDealerPriceAvailability } from 'services/dealerPriceAvailability/utils';
import { PartList } from 'services/availabilityBySource/types';

export interface KitDrawerWrapperProps {
  kitDrawerActive?: boolean;
  setKitDrawerActive?: (drawerActive: boolean) => void;
  data?: CurrentItemsWithPrice;
  priceAvailabilityDrawerData?: DealerPriceAvailabilityResponse;
  dealerInformation?: DealerResponse;
  getEsites;
  availRemoveInterimStockFlag?: boolean;
  isShowSourcingDetailEnabled?: boolean;
  isDeliveryEnabled?: boolean;
  userStoreLocationData?: LocationResponse;
  isHeadlessPDPEnabled?: boolean;
  orderItems?: Array<OrderItemsResponseKit>;
  contactDealerComp?: React.ReactNode;
  cartDetails?: CartResponse;
  priceAvailabilityData;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
  isHeadlessPriceAndAvailability: boolean;
}
const KitDrawerWrapper = ({
  kitDrawerActive,
  setKitDrawerActive,
  data,
  priceAvailabilityDrawerData,
  dealerInformation,
  getEsites,
  availRemoveInterimStockFlag,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  userStoreLocationData,
  isHeadlessPDPEnabled,
  orderItems,
  contactDealerComp,
  cartDetails,
  priceAvailabilityData,
  isRTL = false,
  currencyLdFeatureFlag = false,
  isHeadlessPriceAndAvailability
}: KitDrawerWrapperProps) => {
  const [kitData, setKitData] = useState(data?.components);
  const [subTotal, setSubTotal] = useState({
    formattedSubTotal: '0',
    unformattedSubTotal: 0
  });
  const [isLoadingInitial, setIsLoadingInitial] = useState<boolean>();
  const [isLoadingRow, setIsLoadingRow] = useState<Record<string, boolean>>({});
  const [orderItemsData, setOrderItemsData] = useState(orderItems);
  const [dealerPriceData, setDealerPriceData] = useState<
    Record<string, ProductDealerPriceAvailability>
  >({});
  const currency = String(priceAvailabilityDrawerData?.currency || '');
  const { handleAddToCart } = useCoreContext();
  const toast = useQuickToast();
  const t = useTranslations();
  const handleCloseDrawer = () => {
    setKitDrawerActive(false);
  };
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.BLOCKS_LG);
  const { locale, storeId } = getEsites;
  const { fireEvent: addToCartItemEvent } = useAddToCartItemEvent();
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const itemListNameId = PRIME_PDP_KITS;
  const saudiCurrencySymbol = currencyLdFeatureFlag && checkIsRiyal(currency);

  const getSubtotal = async (
    priceData: Record<string, ProductDealerPriceAvailability>,
    initialLoad?: boolean
  ) => {
    const lineItemsPrices: string[] = Object.values(priceData)
      .filter((item) => item?.totalPrice !== undefined)
      .map((item) =>
        item?.totalPrice
          ? String(item?.totalPrice?.replace(/[^0-9.]/g, ''))
          : '0'
      );
    if (lineItemsPrices?.length === 0) {
      setSubTotal({ formattedSubTotal: '0', unformattedSubTotal: 0 });
      return;
    }
    const requestBody = { currency, lineItemsPrices };
    if (lineItemsPrices?.length > 0) {
      initialLoad && setIsLoadingInitial(true);
      try {
        const subTotalResponse = await getSubTotal(requestBody);
        if (
          subTotalResponse &&
          typeof subTotalResponse?.formattedSubtotal === 'string'
        ) {
          setSubTotal({
            formattedSubTotal: subTotalResponse?.formattedSubtotal,
            unformattedSubTotal: subTotalResponse?.formattedSubtotal
          });
        }
        initialLoad && setIsLoadingInitial(false);
      } catch (err) {
        console.error('Failed to fetch Subtotal', err);
      }
    }
  };

  const fetchPriceHeadlessAndTransform = useCallback(
    async (payload: PartList[]) => {
      try {
        const headlessResult = await fetchPriceHeadless(payload);
        const result = {
          ...headlessResult,
          response:
            headlessResult?.response &&
            transformHeadlessToDealerPriceAvailability(headlessResult.response)
        };
        return result;
      } catch (error) {
        return { error: true };
      }
    },
    []
  );

  useEffect(() => {
    const initialLoad = true; //avoid loader when we change the quantity for individual row
    if (!kitData || kitData?.length === 0) return;
    const fetchInitialPrices = async () => {
      setIsLoadingInitial(true);

      let result: {
        error: boolean;
        response?: DealerPriceAvailabilityResponse;
      };
      if (isHeadlessPriceAndAvailability) {
        result = await fetchPriceHeadlessAndTransform(
          kitData?.map((item) => ({
            partNumber: item.partNumber,
            quantity: item.quantity
          }))
        );
      } else {
        result = await fetchPrice(
          kitData?.map((item) => ({
            partNumber: item.partNumber,
            quantity: item.quantity
          }))
        );
      }

      if (result?.response?.priceAndAvailability) {
        const priceMap: Record<string, ProductDealerPriceAvailability> = {};
        result?.response?.priceAndAvailability.forEach((p) => {
          priceMap[p?.partNumber] = p;
        });
        setDealerPriceData(priceMap);
        await getSubtotal(priceMap, initialLoad);
      }
      setIsLoadingInitial(false);
    };
    fetchInitialPrices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setKitData(data?.components);
  }, [data]);

  const valHandle = debounce(async (e, i) => {
    const updatedQuantity = e.detail.value;
    const numericValue = updatedQuantity;
    const quantity = numericValue > 99999 ? '99999' : numericValue;
    if (!isNaN(numericValue)) {
      setKitData((prev) => {
        const updatedData = prev?.map((item, index) =>
          index === i ? { ...item, quantity } : item
        );
        return updatedData;
      });
      setIsLoadingRow((prev) => ({ ...prev, [i]: true }));
      const updatedItem = { ...kitData[i], quantity };

      try {
        let priceRes: {
          error: boolean;
          response?: DealerPriceAvailabilityResponse;
        };
        if (isHeadlessPriceAndAvailability) {
          priceRes = await fetchPriceHeadlessAndTransform([
            {
              partNumber: updatedItem?.partNumber,
              quantity: updatedItem?.quantity
            }
          ]);
        } else {
          priceRes = await fetchPrice([
            {
              partNumber: updatedItem?.partNumber,
              quantity: updatedItem?.quantity
            }
          ]);
        }

        if (priceRes?.response?.priceAndAvailability) {
          setDealerPriceData((prev) => {
            const updatedPrice = {
              ...prev,
              [updatedItem?.partNumber]:
                priceRes?.response?.priceAndAvailability[0]
            };
            getSubtotal(updatedPrice, false); // Passing false to avoid the loader for row quantity change
            return updatedPrice;
          });
        }
      } catch (err) {
        console.error('Error Fetching dealer price:', err);
      } finally {
        setIsLoadingRow((prev) => ({ ...prev, [i]: false }));
      }
    }
  }, DEBOUNCE_TIME_MILLISECONDS);
  const generatePayload = useCallback(
    (kitItemData) => {
      const getSosCode = (kitPartNumber) => {
        const partNumber =
          priceAvailabilityDrawerData?.priceAndAvailability?.find(
            (a) => a.partNumber === kitPartNumber
          );
        return partNumber?.sosCode;
      };
      if (kitItemData?.length > 1) {
        return kitItemData
          .filter((item) => {
            const availabilityItem =
              priceAvailabilityDrawerData?.priceAndAvailability?.find(
                (a) => a.partNumber === item?.partNumber
              );
            return availabilityItem?.enableAddToCart && item?.quantity > 0;
          })
          .map((item) => ({
            partNumber: item?.partNumber,
            quantity: item?.quantity,
            partName: item?.name ?? item?.name,
            lineItemNote: data?.partNumber,
            sosCode: getSosCode(item?.partNumber)
          }));
      } else {
        return [
          {
            partNumber: kitItemData?.partNumber,
            quantity: kitItemData?.quantity,
            partName: kitItemData?.name ?? kitItemData?.name,
            lineItemNote: data?.partNumber,
            sosCode: kitItemData?.price?.sosCode
          }
        ];
      }
    },
    [priceAvailabilityDrawerData?.priceAndAvailability, data]
  );
  const updateCartItemCount = (singleItem) => {
    setOrderItemsData((prevCart) => {
      const existingItem = prevCart.find(
        (item) => item.partNumber === singleItem?.partNumber
      );
      if (existingItem) {
        return prevCart.map((item) =>
          item?.partNumber === singleItem?.partNumber
            ? { ...item, quantity: item.quantity + singleItem?.quantity }
            : item
        );
      } else {
        const quantity = singleItem?.quantity;
        const partNumber = singleItem?.partNumber;
        return [...prevCart, { quantity: quantity, partNumber: partNumber }];
      }
    });
  };

  const mergedKitResponse = useMemo(() => {
    if (
      !priceAvailabilityData ||
      !priceAvailabilityDrawerData?.priceAndAvailability
    ) {
      return [];
    }
    return priceAvailabilityData.map((item) => {
      const kitResponse =
        priceAvailabilityDrawerData?.priceAndAvailability?.find((priceItem) => {
          return priceItem.partNumber === item.partNumber;
        });
      return kitResponse ? { ...item, ...kitResponse } : { ...item };
    });
  }, [
    priceAvailabilityData,
    priceAvailabilityDrawerData?.priceAndAvailability
  ]);
  const handleAddToCartClick = useCallback(
    async (kitItemData) => {
      const payLoad = generatePayload(kitItemData);
      const response = await handleAddToCart(
        { items: payLoad },
        storeId,
        locale
      );
      if (response?.error) {
        toast.error({ message: t('ITEM_ADD_ERROR') });
      } else if (Array.isArray(kitItemData) && kitItemData.length > 1) {
        setKitDrawerActive(false);
        kitItemData?.map((singleKitItem) => {
          updateCartItemCount(singleKitItem);
        });
        toast.success({ message: t('2483_PDP_ADD_TO_CART_MULTIPLE_MODAL') });
        // Fire events once with the whole array
        fireSelectItemEvent({
          item: getFormattedSearchSelectItem(
            cartDetails?.orderHeader,
            mergedKitResponse
          ),
          partName:
            mergedKitResponse &&
            `${mergedKitResponse?.partNumber}: ${mergedKitResponse?.name}`,
          locationId: storeId,
          itemSummary: mergedKitResponse,
          isSearchPageEvent: true,
          itemListId: itemListNameId,
          itemListName: itemListNameId,
          serialNumber: '',
          fitment: ''
        });
        addToCartItemEvent({
          locationId: storeId,
          itemSummary: mergedKitResponse,
          itemData: getFormattedItemData(
            cartDetails?.orderHeader?.orderId,
            mergedKitResponse,
            currency
          ),
          itemListId: itemListNameId,
          itemListName: itemListNameId
        });
      } else {
        updateCartItemCount(kitItemData);
        toast.success({ message: t('ITEM_ADDED_TO_CART') });

        const getMergedDataForItem = (item) => {
          return (
            mergedKitResponse.find(
              (mergedItem) => mergedItem.partNumber === item.partNumber
            ) || item
          );
        };
        // Fire events for each individual item
        const singleItemArray = Array.isArray(kitItemData)
          ? kitItemData
          : [kitItemData];
        singleItemArray.forEach((item) => {
          const mergedData = getMergedDataForItem(item);
          fireSelectItemEvent({
            item: getFormattedSearchSelectItem(
              cartDetails?.orderHeader,
              mergedData
            ),
            partName:
              mergedData && `${mergedData.partNumber}: ${mergedData.name}`,
            locationId: storeId,
            itemSummary: mergedData,
            isSearchPageEvent: true,
            itemListId: itemListNameId,
            itemListName: itemListNameId,
            serialNumber: '',
            fitment: ''
          });
          addToCartItemEvent({
            locationId: storeId,
            itemSummary: mergedData,
            itemData: getFormattedItemData(
              cartDetails?.orderHeader?.orderId,
              [mergedData],
              currency
            ),
            itemListId: itemListNameId,
            itemListName: itemListNameId
          });
        });
      }
    },
    [
      fireSelectItemEvent,
      addToCartItemEvent,
      currency,
      itemListNameId,
      locale,
      cartDetails,
      storeId,
      mergedKitResponse,
      toast,
      t,
      handleAddToCart,
      generatePayload,
      setKitDrawerActive
    ]
  );
  const isAddAllToCartDisabled =
    !priceAvailabilityDrawerData?.priceAndAvailability?.some(
      (product) => product?.enableAddToCart
    ) || kitData.every((item) => Number(item.quantity) === 0);

  const saudiRiyalCheck = () => {
    return saudiCurrencySymbol
      ? getRiyalFormattedPrice({
          price: subTotal?.formattedSubTotal,
          priceWrapperClasses: 'px-1',
          isRTL: isRTL
        })
      : `${subTotal?.formattedSubTotal} ${currency}`;
  };

  const commonProps = {
    kitData,
    valHandle,
    priceAvailabilityDrawerData,
    handleAddToCart: handleAddToCartClick,
    dealerInformation,
    locale,
    availRemoveInterimStockFlag,
    isShowSourcingDetailEnabled,
    isDeliveryEnabled,
    userStoreLocationData,
    isHeadlessPDPEnabled,
    orderItems: orderItemsData,
    contactDealerComp,
    dealerPriceData,
    isLoadingInitial,
    isLoadingRow,
    mergedKitResponse,
    cartDetails,
    storeId,
    isRTL,
    currencyLdFeatureFlag
  };
  return (
    <CatDrawer
      isActive={kitDrawerActive}
      onBlDrawerClose={handleCloseDrawer}
      size="lg"
      className={styles['kit-drawer-size']}
    >
      <CatHeading variant="title" slot="header">
        {t('CUSTOMIZE_LAYOUT') + ' ' + data.partNumber + ': ' + data.name}
      </CatHeading>
      {isLG ? (
        <KitDetailsTableBody {...commonProps} />
      ) : (
        <KitMobile {...commonProps} />
      )}
      <div slot="footer">
        <label className={styles['note-font']} htmlFor="note">
          {t('QUANTITY_NOTE')}
        </label>
        <hr />

        <div className="d-flex align-items-start">
          <div className={cx('px-1 py-2', styles['kit-total-price'])}>
            <strong>
              {t('TOTAL_PRICE')}:
              {isLoadingInitial ||
              Object.values(isLoadingRow).some((val) => val) ? (
                <SkeletonLoader />
              ) : (
                saudiRiyalCheck()
              )}
            </strong>
          </div>
        </div>
        <CatButton
          variant="primary"
          disabled={isAddAllToCartDisabled}
          onClick={() => handleAddToCartClick(kitData)}
        >
          {t('CAT_PD_ADD_ALL_TO_CART')}
        </CatButton>
        <CatButton variant="ghost" onClick={handleCloseDrawer}>
          {t('CANCEL')}
        </CatButton>
      </div>
    </CatDrawer>
  );
};
export default KitDrawerWrapper;
